import cx from 'classnames';
import { Drawer } from 'restaurantAdmin/components/drawer/Drawer';
import styles from './MessagingDrawer.scss';
import typography from '~styles/typography.scss';
import { Button, ButtonVariants } from '@components/button/Button';
import { useFieldArray, useForm } from 'react-hook-form';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { useMessagingDrawer, useSendMassMessage } from './hooks';
import { useQueries } from '@tanstack/react-query';
import { getGuest } from './apiHelpers';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { Spinner } from '@components/Spinner';
import type { GuestsApiGuest } from 'restaurantAdmin/guestBook/apiHelpers';
import { useEffect } from 'react';

type FormValues = {
  recipients: GuestsApiGuest[];
  message: string;
};

const MassMessageMessagingForm = ({
  guests,
  numberOfWalkIns,
  closeMessagingDrawer,
}: {
  guests: GuestsApiGuest[];
  numberOfWalkIns: number;
  closeMessagingDrawer: () => void;
}) => {
  const guestsWithPhone = guests.filter((guest) => guest.phone);
  const guestsWithoutPhone = guests.filter((guest) => !guest.phone);
  const hasWalkIns = numberOfWalkIns > 0;
  const hasGuestsWithoutPhone = guestsWithoutPhone.length > 0;
  const { mutate: sendMassMessageMutation, isPending: isSendingMessage } =
    useSendMassMessage(closeMessagingDrawer);

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {
      recipients: guestsWithPhone,
      message: '',
    },
  });

  const messageValue = watch('message');
  const isMessageEmpty = !messageValue?.trim();

  const { fields: recipients } = useFieldArray({
    control,
    name: 'recipients',
  });

  const guestsKey = guests
    .map((guest) => guest.id)
    .sort()
    .join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: what we did was correct
  useEffect(() => {
    reset({ recipients: guestsWithPhone });
  }, [guestsKey]);

  const onSubmit = (data: FormValues) => {
    sendMassMessageMutation({
      message: data.message,
      userIds: data.recipients.map((recipient) => recipient.id),
    });
  };

  if (!guestsWithPhone.length) {
    return (
      <div className={styles.errorMessage}>
        None of the guests you selected have a phone number. To send a message,
        please select at least one guest with a phone number.
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={styles.multipleGuestMessageForm}
    >
      <div className={styles.massMessageOccupants}>
        <h3 className={typography.c2_20}>{recipients.length} Recipients</h3>
        <ul className={styles.massMessageOccupantsList} aria-label="Recipients">
          {recipients.map((recipient) => (
            <li key={recipient.id} className={styles.recipientPill}>
              <span className={typography.t2}>
                {recipient.firstName} {recipient.lastName}
              </span>
            </li>
          ))}
        </ul>
        {hasGuestsWithoutPhone && (
          <p
            className={cx(
              typography.t3,
              styles.multipleGuestsMessageInfo,
              styles.error,
            )}
          >
            There is no phone number on record for:{' '}
            {guestsWithoutPhone
              .map((guest) => `${guest.firstName} ${guest.lastName}`)
              .join(', ')}
            .
          </p>
        )}
        {hasWalkIns && (
          <p
            className={cx(
              typography.t3,
              styles.multipleGuestsMessageInfo,
              styles.error,
            )}
          >
            Unable to message walk-ins ({numberOfWalkIns} selected).
          </p>
        )}
      </div>
      <div className={cx(styles.messageInput, styles.multipleGuests)}>
        <ControlledFormTextareaAutosize
          control={control}
          label="SMS Message"
          name="message"
          placeholder="Type your SMS message here..."
          minRows={10}
          maxRows={10}
          rules={{
            validate: (value) =>
              typeof value === 'string' && value.trim().length > 0
                ? true
                : 'Message is required',
          }}
        />
        <p className={cx(typography.t3, styles.multipleGuestsMessageInfo)}>
          This text message will be sent to the selected guests individually.
        </p>
        <Button
          type="submit"
          label="Send Message"
          variant={ButtonVariants.Primary}
          className={styles.messageInputButton}
          isDisabled={isSendingMessage || isMessageEmpty}
        />
      </div>
    </form>
  );
};

export const MassMessageMessagingDrawer = ({
  guestIds,
  walkInIds,
  isOpen,
}: {
  guestIds: string[];
  walkInIds: string[];
  isOpen: boolean;
}) => {
  const restaurant = useRestaurant();
  const { closeMessagingDrawer } = useMessagingDrawer();

  const results = useQueries({
    queries: guestIds.map((guestId) => ({
      queryKey: ['guest', guestId],
      queryFn: () => getGuest(restaurant.id, guestId),
    })),
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeMessagingDrawer();
    }
  };

  const isLoading = results.some((result) => result.isPending);
  const guests = results
    .map((result) => result.data)
    .filter((guest) => guest !== undefined);

  return (
    <Drawer
      modal={false}
      side="right"
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      {isLoading ? (
        <Spinner className={styles.spinner} />
      ) : (
        <div className={styles.container}>
          <header className={styles.header}>
            <h2 className={typography.h4}>Text Message Guests</h2>

            <div>
              <Button
                label="Cancel"
                onClick={closeMessagingDrawer}
                variant={ButtonVariants.Tertiary}
              />
            </div>
          </header>

          <MassMessageMessagingForm
            guests={guests}
            numberOfWalkIns={walkInIds.length}
            closeMessagingDrawer={closeMessagingDrawer}
          />
        </div>
      )}
    </Drawer>
  );
};
